import React from 'react'
import ReactMarkdown, { Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'

import Box from 'components_new/atoms/Box'
import Divider from 'components_new/atoms/Divider'
import Link from 'components_new/atoms/Link'
import Table from 'components_new/atoms/Table'
import TableBody from 'components_new/atoms/Table/TableBody'
import TableCell from 'components_new/atoms/Table/TableCell'
import TableContainer from 'components_new/atoms/Table/TableContainer'
import TableHead from 'components_new/atoms/Table/TableHead'
import TableRow from 'components_new/atoms/Table/TableRow'
import Text from 'components_new/atoms/Text'

interface MarkdownContentProps {
  children: string
}

// Renderers mirror the RichTextEditor element/leaf styling so markdown content
// matches rich-text content (see RichTextEditor/renders Element + Leaf).
// Hoisted to module scope so react-markdown sees stable component references.

const REMARK_PLUGINS = [remarkGfm]

// Rich text only has heading-two (h5 + bottom border) and heading-three (h6).
// Map h1/h2 to the heading-two style and h3/h4 to the heading-three style.
const HeadingTwo =
  (component: 'h1' | 'h2'): Components['h1'] =>
    ({ children }) =>
      (
        <Text
          color="inherit"
          component={component}
          gutterBottom
          sx={{
            borderBottom: '1px solid',
            borderBottomColor: 'divider',
            pb: '0.5rem',
            ':not(:first-child)': { mt: '2em' }
          }}
          variant="h5"
        >
          {children}
        </Text>
      )

const HeadingThree =
  (component: 'h3' | 'h4'): Components['h3'] =>
    ({ children }) =>
      (
        <Text
          color="inherit"
          component={component}
          gutterBottom
          sx={{
            pb: '0.5rem',
            ':not(:first-child)': { mt: '2em' }
          }}
          variant="h6"
        >
          {children}
        </Text>
      )

const P: Components['p'] = ({ children }) => (
  <Text
    color="inherit"
    sx={{
      ':not(:first-of-type)': { mt: '1.25em' },
      ':not(:last-of-type)': { mb: '1.25em' }
    }}
    variant="body1"
  >
    {children}
  </Text>
)

const Strong: Components['strong'] = ({ children }) => (
  <strong>{children}</strong>
)

const Em: Components['em'] = ({ children }) => <em>{children}</em>

const Ul: Components['ul'] = ({ children }) => (
  <Text
    component="ul"
    sx={{ listStyleType: 'disc', my: '1.25em', pl: '1.625em' }}
    variant="body1"
  >
    {children}
  </Text>
)

const Ol: Components['ol'] = ({ children }) => (
  <Text
    component="ol"
    sx={{ listStyleType: 'decimal', my: '1.25em', pl: '1.625em' }}
    variant="body1"
  >
    {children}
  </Text>
)

const Li: Components['li'] = ({ children }) => (
  <Box
    component="li"
    sx={{ my: '0.5em', pl: '0.375em', '&::marker': { color: '#bababb' } }}
  >
    {children}
  </Box>
)

const Code: Components['code'] = ({ children }) => <code>{children}</code>

const Pre: Components['pre'] = ({ children }) => (
  <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap' }}>
    {children}
  </Box>
)

const Hr: Components['hr'] = () => <Divider sx={{ my: 4 }} />

// Tables and links have no RichTextEditor equivalent, but GFM markdown can
// emit them — keep lightweight renderers so they degrade gracefully.
const Tbl: Components['table'] = ({ children }) => (
  <TableContainer
    sx={{
      mb: 1,
      borderRadius: 1,
      border: '1px solid',
      borderColor: 'divider'
    }}
  >
    <Table size="small">{children}</Table>
  </TableContainer>
)
const Thead: Components['thead'] = ({ children }) => (
  <TableHead>{children}</TableHead>
)
const Tbody: Components['tbody'] = ({ children }) => (
  <TableBody>{children}</TableBody>
)
const Tr: Components['tr'] = ({ children }) => <TableRow>{children}</TableRow>
const Th: Components['th'] = ({ children }) => (
  <TableCell
    variant="head"
    sx={{ fontWeight: 'bold', bgcolor: 'action.hover' }}
  >
    {children}
  </TableCell>
)
const Td: Components['td'] = ({ children }) => <TableCell>{children}</TableCell>

const A: Components['a'] = ({ href, children }) => (
  <Link href={href} target="_blank" variant="body1">
    {children}
  </Link>
)

const COMPONENTS: Components = {
  h1: HeadingTwo('h1'),
  h2: HeadingTwo('h2'),
  h3: HeadingThree('h3'),
  h4: HeadingThree('h4'),
  p: P,
  strong: Strong,
  em: Em,
  ul: Ul,
  ol: Ol,
  li: Li,
  code: Code,
  pre: Pre,
  hr: Hr,
  table: Tbl,
  thead: Thead,
  tbody: Tbody,
  tr: Tr,
  th: Th,
  td: Td,
  a: A
}

const MarkdownContentInner = ({ children }: MarkdownContentProps) => {
  return (
    <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={COMPONENTS}>
      {children}
    </ReactMarkdown>
  )
}

/**
 * Renders an assistant message's markdown (GFM) using the same styling as the
 * RichTextEditor renderers, so markdown and rich-text content look alike.
 */
const MarkdownContent = React.memo(
  MarkdownContentInner,
  (prev, next) => prev.children === next.children
)

MarkdownContent.displayName = 'MarkdownContent'

export default MarkdownContent
